// Annet salg: belop, MVA per sats og oppgjor ut fra de lagrede salgslinjene.
// Ren beregning uten avhengigheter, slik at foringen, MVA-rapporten, kvitteringen
// og utkastlisten regner likt. Twin: functions/accounting/otherSaleAmounts.ts.
(function initOtherSaleAmounts(global) {
  const ZERO_ACCOUNT_TREATMENTS = ['salesReverseCharge51', 'export52', 'zeroRated', 'exempt'];
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const money = (value) => Math.sign(num(value)) * Math.round(Math.abs(num(value)) * 100 + 1e-8) / 100;
  const text = (value) => String(value ?? '').trim();
  const treatmentOf = (entry) => text(entry?.vatTreatment) || (num(entry?.vatRate) === 0 ? 'zeroRated' : 'standard');
  const defaultSalesAccount = (rawTreatment) => (ZERO_ACCOUNT_TREATMENTS.includes(text(rawTreatment)) ? '3100' : '3000');
  const isProductLine = (line) => text(line?.lineType || line?.documentLineType || 'product') === 'product';
  const hasStoredNet = (line) => line?.amountNet !== undefined && line?.amountNet !== null && line?.amountNet !== '' && Number.isFinite(Number(line.amountNet));

  // Linjer lagret av salgseditoren (med netto etter rabatt per linje). Eldre salg
  // har ingen linjer — da er toppnivafeltene salget, som for.
  const productLines = (sale) => (Array.isArray(sale?.lines) ? sale.lines.filter((line) => line && isProductLine(line)) : []);
  const linesProblem = (sale) => {
    const lines = productLines(sale);
    if (!lines.some(hasStoredNet)) return null;
    if (lines.some((line) => !hasStoredNet(line) || num(line.amountNet) < 0)) return 'invalid-line';
    const total = money(lines.reduce((sum, line) => sum + num(line.amountNet), 0));
    return Math.abs(total - money(sale?.amountNet)) > 0.005 ? 'sum-mismatch' : null;
  };
  const storedLines = (sale) => {
    const lines = productLines(sale);
    return lines.length && lines.every(hasStoredNet) && !linesProblem(sale) ? lines : [];
  };

  const vatLines = (sale) => {
    const lines = storedLines(sale);
    const source = lines.length ? lines : [sale || {}];
    return source.map((entry) => {
      const vatTreatment = treatmentOf(entry);
      return {
        amountNet: money(entry.amountNet),
        vatTreatment,
        vatRate: vatTreatment === 'standard' ? num(entry.vatRate) : 0,
        salesAccountCode: text(entry.salesAccountCode) || defaultSalesAccount(entry.vatTreatment),
      };
    });
  };

  // Ett salgsbelop per inntektskonto og MVA-behandling/sats.
  const groups = (sale) => {
    const byKey = new Map();
    vatLines(sale).forEach((line) => {
      const key = `${line.salesAccountCode}|${line.vatTreatment}|${line.vatRate}`;
      const current = byKey.get(key) || { ...line, amountNet: 0 };
      current.amountNet = money(current.amountNet + line.amountNet);
      byKey.set(key, current);
    });
    return [...byKey.values()].filter((group) => Math.abs(group.amountNet) > 0.005);
  };

  // Utgaende MVA rundes per sats, ikke per linje.
  const vatByRate = (sale) => {
    const byRate = new Map();
    vatLines(sale).filter((line) => line.vatTreatment === 'standard' && line.vatRate > 0).forEach((line) => {
      byRate.set(line.vatRate, money(num(byRate.get(line.vatRate)) + line.amountNet));
    });
    return [...byRate.entries()].map(([rate, base]) => ({ rate, base, vat: money(base * rate / 100) })).filter((entry) => entry.vat);
  };

  const totals = (sale) => {
    if (text(sale?.vatTreatment) === 'vatOnly') {
      const amount = money(sale?.amountNet);
      const fee = Math.min(amount, Math.max(0, money(sale?.settlementFeeAmount || sale?.feeAmount)));
      return { net: amount, vat: 0, gross: amount, fee, payout: money(amount - fee) };
    }
    const net = money(vatLines(sale).reduce((sum, line) => sum + line.amountNet, 0));
    const vat = money(vatByRate(sale).reduce((sum, entry) => sum + entry.vat, 0));
    const gross = money(net + vat);
    const fee = Math.min(gross, Math.max(0, money(sale?.settlementFeeAmount || sale?.feeAmount)));
    return { net, vat, gross, fee, payout: money(gross - fee) };
  };
  const splits = (sale) => (Array.isArray(sale?.paymentSplits) ? sale.paymentSplits : []).filter((split) => num(split?.amount) > 0.005);
  // Delbetalingene skal dekke det som faktisk kom inn: brutto minus gebyr.
  const splitMismatch = (sale) => {
    const list = splits(sale);
    if (!list.length) return null;
    const expected = totals(sale).payout;
    const actual = money(list.reduce((sum, split) => sum + num(split.amount), 0));
    return Math.abs(actual - expected) > 0.005 ? { expected, actual } : null;
  };

  const vatSignature = (sale) => JSON.stringify(text(sale?.vatTreatment) === 'vatOnly'
    ? { vatOnly: money(sale?.amountNet) }
    : groups(sale).map(({ vatTreatment, vatRate, amountNet }) => [vatTreatment, vatRate, amountNet]));

  global.PlummeOtherSaleAmounts = { money, storedLines, linesProblem, vatLines, groups, vatByRate, totals, splits, splitMismatch, vatSignature };
})(typeof window !== 'undefined' ? window : globalThis);
